<template>
  <vue-pdf-embed
    :id="`vue-pdf-embed-${id}`"
    class="group relative w-full"
    :source="file.url"
    @rendered="getScaleFactor"
  >
    <template v-if="showEditOverlay" #after-page>
      <div
        class="edit-overlay group absolute top-0 right-0 bottom-0 left-0 z-20"
      >
        <template v-for="(page, pageIndex) in orderedPages">
          <div
            v-for="instance in page.instances"
            :key="instance.text"
            v-tooltip="instance.text"
            class="absolute bg-red-500/10 text-white"
            :style="getInstanceStyle(instance.bounding_boxes[0], pageIndex)"
          ></div>
        </template>
      </div>
    </template>
  </vue-pdf-embed>
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";
import VuePdfEmbed from "vue-pdf-embed";

import cloneDeep from "lodash/cloneDeep";

import { InscribeReportKey } from "@/helpers/symbols";

import type { BoundingBox } from "@/models/commercialData/Inscribe";

const props = defineProps({
  file: {
    type: Object,
    required: true,
  },
  id: {
    type: [String, Number],
    required: false,
  },
});

const report = inject(InscribeReportKey);
const scaleFactor = ref(1);
const pageHeight = ref(0);
const showEditOverlay = ref(false);

const getScaleFactor = () => {
  const pdfEmbed = document.getElementById(`vue-pdf-embed-${props.id}-1`);
  pageHeight.value = pdfEmbed?.clientHeight ?? 0;
  const style = pdfEmbed?.style;

  scaleFactor.value = style?.getPropertyValue("--scale-factor")
    ? Number(style?.getPropertyValue("--scale-factor"))
    : 1;
  showEditOverlay.value = true;
};

const orderedPages = computed(() => {
  if (!report?.fraud_results?.edited_text?.details?.pages) {
    return [];
  }
  return cloneDeep(report?.fraud_results?.edited_text?.details?.pages).sort(
    (a, b) => a.page_number - b.page_number
  );
});

const getInstanceStyle = (
  bounding_boxes: BoundingBox,
  page_index: number
): Pick<CSSStyleDeclaration, "top" | "height" | "left" | "width"> => {
  // due to limitations of the pdf-embed library, we need to calculate the offsetTop
  // to determine how far down the absolute positioned elements should be
  // since it is one absolute positioned div covering the entire document (all pages)
  const offsetTop = (pageHeight.value ?? 0) * page_index;
  const top = bounding_boxes.top * scaleFactor.value + offsetTop;
  const bottom = bounding_boxes.bottom * scaleFactor.value + offsetTop;
  const height = bottom - top;
  const left = bounding_boxes.x0 * scaleFactor.value;
  const right = bounding_boxes.x1 * scaleFactor.value;
  const width = right - left;
  const style = {
    top: `${top}px`,
    height: `${height}px`,
    left: `${left}px`,
    width: `${width}px`,
  };
  return style;
};
</script>
